import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { SMark } from "@/components/Logo";
import { Menu, X } from "lucide-react";

const Navbar = ({ hideAtTop = false, darkTheme = false }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [scrolledPastGate, setScrolledPastGate] = useState(!hideAtTop);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 8);
      setScrolledPastGate(hideAtTop ? y > 120 : true);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [hideAtTop]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const linkColor = darkTheme
    ? "text-white hover:text-[#22C585]"
    : "text-[#1E294E] hover:text-[#15A76C]";
  const activeColor = darkTheme ? "text-[#22C585]" : "text-[#15A76C]";

  const linkClass = (path) =>
    `text-sm font-medium transition-colors ${
      location.pathname === path ? activeColor : linkColor
    }`;

  const barStyle = darkTheme
    ? {
        background: "rgba(7,26,53,0.85)",
        backdropFilter: "blur(18px) saturate(140%)",
        WebkitBackdropFilter: "blur(18px) saturate(140%)",
        border: "1px solid rgba(255,255,255,0.1)",
        boxShadow: "0 10px 30px -15px rgba(0,0,0,0.5)",
      }
    : undefined;

  const isVisible = scrolledPastGate;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "py-2" : "py-4"
      } ${
        isVisible
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 -translate-y-6 pointer-events-none"
      }`}
      data-testid="sn-navbar"
      aria-hidden={!isVisible}
    >
      <div className="mx-auto max-w-[1200px] px-4 md:px-6">
        <div
          className={`${
            darkTheme ? "" : "sn-glass"
          } rounded-2xl px-4 md:px-6 py-3 flex items-center justify-between transition-all ${
            scrolled ? "shadow-lg" : ""
          }`}
          style={barStyle}
        >
          <Link
            to="/"
            aria-label="SpendNova home"
            data-testid="sn-nav-home"
            className="relative inline-flex items-center justify-center rounded-xl overflow-visible"
            style={{
              background: "#071A35",
              padding: "6px 12px",
              border: "1px solid rgba(255,255,255,0.1)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
            }}
          >
            <img
              src="/spendnova-logo-2400.webp"
              alt="SpendNova™"
              draggable="false"
              className="select-none"
              style={{ height: 26, width: "auto", display: "block" }}
            />
            <span
              aria-hidden="true"
              className="absolute text-white font-semibold pointer-events-none select-none"
              style={{
                right: 2,
                top: 2,
                fontSize: 9,
                lineHeight: 1,
              }}
            >
              ™
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-7">
            <a
              href="/#safe-to-spend"
              className={linkClass("/#safe-to-spend")}
              data-testid="sn-nav-whats-inside"
            >
              What's Inside
            </a>
            <a
              href="/#the-promise"
              className={linkClass("/#the-promise")}
              data-testid="sn-nav-promise"
            >
              The Promise
            </a>
            <Link
              to="/install"
              className={linkClass("/install")}
              data-testid="sn-nav-install"
            >
              Install
            </Link>
            <Link
              to="/privacy"
              className={linkClass("/privacy")}
              data-testid="sn-nav-privacy"
            >
              Privacy
            </Link>
            <Link
              to="/terms"
              className={linkClass("/terms")}
              data-testid="sn-nav-terms"
            >
              Terms
            </Link>
            <Link
              to="/support"
              className={linkClass("/support")}
              data-testid="sn-nav-support"
            >
              Support
            </Link>
          </nav>

          <div className="hidden md:block">
            <a
              href="/#waitlist"
              className="sn-btn-primary inline-flex items-center px-4 py-2 rounded-xl text-sm font-semibold"
              data-testid="sn-nav-cta"
            >
              Join Waitlist
            </a>
          </div>

          <button
            className={`md:hidden p-2 rounded-lg ${
              darkTheme ? "text-white" : "text-[#1E294E]"
            }`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="sn-nav-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div
            className={`md:hidden mt-2 rounded-2xl p-4 flex flex-col gap-3 ${
              darkTheme ? "" : "sn-glass"
            }`}
            style={barStyle}
            data-testid="sn-mobile-menu"
          >
            <a
              href="/#safe-to-spend"
              className={darkTheme ? "text-white font-medium" : "text-[#1E294E] font-medium"}
            >
              What's Inside
            </a>
            <a
              href="/#the-promise"
              className={darkTheme ? "text-white font-medium" : "text-[#1E294E] font-medium"}
            >
              The Promise
            </a>
            <Link
              to="/install"
              className={darkTheme ? "text-white font-medium" : "text-[#1E294E] font-medium"}
            >
              Install
            </Link>
            <Link
              to="/privacy"
              className={darkTheme ? "text-white font-medium" : "text-[#1E294E] font-medium"}
            >
              Privacy
            </Link>
            <Link
              to="/terms"
              className={darkTheme ? "text-white font-medium" : "text-[#1E294E] font-medium"}
            >
              Terms
            </Link>
            <Link
              to="/support"
              className={darkTheme ? "text-white font-medium" : "text-[#1E294E] font-medium"}
            >
              Support
            </Link>
            <a
              href="/#waitlist"
              className="sn-btn-primary inline-flex items-center justify-center px-4 py-2 rounded-xl text-sm font-semibold"
            >
              Join Waitlist
            </a>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
